import {
  Alert,
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  FilePondErrorDescription,
  FilePondFile,
  registerPlugin,
} from 'filepond';
import FilePondPluginFileEncode from 'filepond-plugin-file-encode';
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size';
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';
import 'filepond/dist/filepond.min.css';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { truncateFilename } from '../../../helpers/knowledgeHelper';
import { useKnowledgesWithAttachment } from '../../../hooks/queries/useKnowledgesWithAttachment';
import useKnowledgeLanguageOptions from '../../../hooks/useKnowledgeLanguageOptions';
import { useSnackBar } from '../../../hooks/useSnackBar';
import { useTranslation } from '../../../hooks/useTranslation';
import {
  KnowledgeFileData,
  KnowledgeFileMap,
  KnowledgeMode,
} from '../../../interfaces/Knowledge';
import StyledFormItem from '../../../styles/StyledFormItem';
import KnowledgeFileSearchBar from '../../knowledges/KnowledgeFileSearchBar/KnowledgeFileSearchBar';
import StyledFileName from '../../knowledges/Table/StyledFileName';
import StyledKnowledgeItems from '../../knowledges/Table/StyledKnowledgeItems';
import Accordion from '../../shared/Accordion/Accordion';
import Dropdown from '../../shared/DropDown/DropDown';
import DeleteIcon from '../../shared/Icons/DeleteIcon';
import DownloadIcon from '../../shared/Icons/DownloadIcon';
import StyledDeleteIcon from '../../shared/Icons/StyledDeleteIcon';
import Input from '../../shared/Input/Input';
import ResourceAccessFields from '../../shared/ResourceAccessFields/ResourceAccessFields';
import Text from '../../shared/Text/Text';
import WebscrapConfigFields from '../../shared/WebscrapConfigFields/WebscrapConfigFields';
import WorkflowStepLabel from '../../shared/WorkflowStepLabel/WorkflowStepLabel';
import StyledFilePond from './StyledFilePond';

registerPlugin(FilePondPluginFileEncode);
registerPlugin(FilePondPluginFileValidateSize);
registerPlugin(FilePondPluginFileValidateType);

const MAX_FILES = 250;

interface AgentKnowledgeStepProps {
  description?: string;
  label?: string;
  isKnowledgeFilesLoading?: boolean;
  knowledgeFiles?: KnowledgeFileMap; // existing files attached to knowledge
  knowledgeFilesData: KnowledgeFileData[]; // new files for uploading
  stepLabel?: string;
  pendingDeletions?: string[]; // existing file names marked for deletion (hidden, shown with an Undo action, until Save)
  deleteKnowledgeFileCallback?: (fileName: string) => void; // marks an existing file for deletion - no network call is made here
  unmarkKnowledgeFileForDeletion?: (fileName: string) => void; // undoes a pending mark-for-deletion
  downloadKnowledgeFileCallback?: (fileName: string) => void;
  setKnowledgeFilesData: Dispatch<SetStateAction<KnowledgeFileData[]>>;
  setIsKnowledgeFilesLoading: Dispatch<SetStateAction<boolean>>;
  onFieldChange?: (field: string, value: string) => void;
  showAccessFields?: boolean;
  ownerUserId?: string;
  currentUserId?: string;
  // Only agent-creation contexts (call/chat agent forms) let the user pick
  // an existing knowledge base or go without one; the standalone
  // KnowledgeForm page is for authoring a knowledge base itself, so it never
  // shows this choice.
  allowExistingKnowledgeSelection?: boolean;
  // EDIT of an agent that already has a knowledge base: the "create" choice
  // then means "keep editing the attached one", and is labelled as such.
  hasAttachedKnowledge?: boolean;
  // True while a webscrap run is rewriting this knowledge base's files (see
  // Knowledge.webscrap_in_progress). Blocks new uploads and deletions so a
  // human edit can't race the scrape's own add/retire of files.
  disableFileControls?: boolean;
  // Shown on both create and edit. The create endpoint doesn't accept a
  // webscrap config, so the create submit helpers save it with a follow-up
  // update (saveWebscrapConfig) once the new knowledge id exists. Hidden
  // when reusing an existing knowledge base, which this form doesn't own.
  showWebscrapConfig?: boolean;
  // Id of the saved knowledge base whose files are listed. When set, a search
  // bar finds the files that contain a sentence, link or name.
  knowledgeId?: string;
}

const AgentKnowledgeStep = ({
  description,
  label,
  isKnowledgeFilesLoading,
  knowledgeFiles,
  knowledgeFilesData,
  stepLabel,
  pendingDeletions = [],
  deleteKnowledgeFileCallback,
  unmarkKnowledgeFileForDeletion,
  downloadKnowledgeFileCallback,
  setKnowledgeFilesData,
  setIsKnowledgeFilesLoading,
  onFieldChange,
  showAccessFields = false,
  ownerUserId,
  currentUserId,
  allowExistingKnowledgeSelection = false,
  hasAttachedKnowledge = false,
  disableFileControls = false,
  showWebscrapConfig = false,
  knowledgeId,
}: AgentKnowledgeStepProps) => {
  const knowledgeLanguageOptions = useKnowledgeLanguageOptions();
  const { t } = useTranslation();
  const showSnackBar = useSnackBar();
  const [files, setFiles] = useState<File[]>([]);
  const filePondRef = useRef<any>(null);
  const {
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();
  const { data: existingKnowledges = [] } = useKnowledgesWithAttachment(
    currentUserId,
    allowExistingKnowledgeSelection
  );
  const existingKnowledgeOptions = existingKnowledges.map(knowledge => ({
    label:
      knowledge.attachmentCount > 0
        ? t(
            knowledge.attachmentCount === 1
              ? '{{name}} (used by 1 agent)'
              : '{{name}} (used by {{count}} agents)',
            { name: knowledge.name, count: knowledge.attachmentCount }
          )
        : knowledge.name,
    value: knowledge.id ?? '',
  }));

  // Read from the form (not local state) so EDIT and session recovery open
  // on the stored choice. Unset means "create", the pre-existing default.
  const knowledgeMode: KnowledgeMode = allowExistingKnowledgeSelection
    ? (watch('knowledge_mode') ?? 'create')
    : 'create';
  const usingExistingKnowledge = knowledgeMode === 'existing';
  const showKnowledgeFields = knowledgeMode === 'create';

  const onKnowledgeModeChange = (nextMode: KnowledgeMode) => {
    setValue('knowledge_mode', nextMode, { shouldDirty: true });
    if (nextMode !== 'existing') {
      setValue('existing_knowledge_id', '');
    }
  };
  const [filesNames, setFileNames] = useState<KnowledgeFileData[]>([]);
  // While search results are shown they replace the upload area and file lists
  const [isSearchActive, setIsSearchActive] = useState(false);
  // Ref tracks new-file count synchronously so rapid multi-file selection can't bypass the limit check via stale state
  const newFileCountRef = useRef(0);

  const existingFilesCount = Object.entries(knowledgeFiles ?? {}).filter(
    ([key]) => !pendingDeletions.includes(key)
  ).length;
  const totalFileCount = filesNames.length + existingFilesCount;

  const sortedFileEntries = Object.entries(knowledgeFiles ?? {}).sort(
    ([keyA], [keyB]) => keyA.localeCompare(keyB)
  );
  const manualFileEntries = sortedFileEntries.filter(
    ([, value]) => value.source !== 'webscrap'
  );
  const scrapedFileEntries = sortedFileEntries.filter(
    ([, value]) => value.source === 'webscrap'
  );

  // Initialize files state with existing knowledgeFilesData from IndexDB
  useEffect(() => {
    if (knowledgeFilesData) {
      setFileNames([...knowledgeFilesData]);
      newFileCountRef.current = knowledgeFilesData.length;
    }
  }, [knowledgeFilesData]);

  // Watch form fields and trigger onFieldChange for form persistence
  const watchedFields = watch(['knowledge_name', 'knowledge_language']);

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange('knowledge_name', watchedFields[0]);
      onFieldChange('knowledge_language', watchedFields[1]);
    }
  }, [watchedFields, onFieldChange]);

  const handleAddFile = async (
    error: FilePondErrorDescription | null,
    file: FilePondFile | null
  ) => {
    if (error || !file) {
      return;
    }

    // Belt-and-suspenders: the FilePond `disabled` prop already blocks browse/drop,
    // but this guards any path that could still call onaddfile while blocked.
    if (disableFileControls) {
      if (filePondRef.current) {
        filePondRef.current.removeFile(file);
      }
      setIsKnowledgeFilesLoading(false);
      return;
    }

    try {
      const originalFileName = file.file?.name;
      if (!originalFileName) return;

      const fileContent = file.getFileEncodeBase64String();

      // Check if file exists in existing knowledge files
      const fileExistsInKnowledgeFiles =
        knowledgeFiles && originalFileName in knowledgeFiles;

      // Check if file already exists in newly added files
      const fileExistsInNewFiles = filesNames.some(
        file => file.name === originalFileName
      );

      const willReplace = fileExistsInKnowledgeFiles || fileExistsInNewFiles;

      // Use ref-based count so concurrent onaddfile calls from multi-file selection see the up-to-date total
      const currentTotalBeforeAdd =
        newFileCountRef.current + existingFilesCount;

      // Block additions that would exceed the file limit
      if (!willReplace && currentTotalBeforeAdd >= MAX_FILES) {
        if (filePondRef.current) {
          filePondRef.current.removeFile(file);
        }
        showSnackBar(
          t('File limit of {{max}} reached. Remove files to add more.', {
            max: MAX_FILES,
          }),
          'error',
          6000
        );
        setIsKnowledgeFilesLoading(false);
        return;
      }

      // Mark the existing file for deletion if it has the same name (replaced by this upload)
      if (fileExistsInKnowledgeFiles && deleteKnowledgeFileCallback) {
        deleteKnowledgeFileCallback(originalFileName);
      }

      // Remove from newly added files if duplicate
      if (fileExistsInNewFiles) {
        // Remove the old file from FilePond using its API
        if (filePondRef.current) {
          const filePondFiles = filePondRef.current.getFiles();
          const oldFile = filePondFiles.find(
            (f: FilePondFile) => f.file?.name === originalFileName
          );
          if (oldFile) {
            filePondRef.current.removeFile(oldFile);
          }
        }

        setKnowledgeFilesData(previousFiles =>
          previousFiles.filter(file => file.name !== originalFileName)
        );
      }

      // Add a new file for upload
      setKnowledgeFilesData(previousFiles => [
        ...previousFiles,
        {
          name: originalFileName,
          content: fileContent,
        },
      ]);

      if (!willReplace) {
        newFileCountRef.current += 1;
      }

      const newTotal = willReplace
        ? currentTotalBeforeAdd
        : currentTotalBeforeAdd + 1;
      if (newTotal >= MAX_FILES) {
        showSnackBar(
          t(
            'File limit reached ({{current}}/{{max}}). No more files can be added.',
            { current: newTotal, max: MAX_FILES }
          ),
          'warning',
          6000
        );
      } else if (newTotal >= MAX_FILES - 25) {
        showSnackBar(
          t(
            'Uploading "{{filename}}" — approaching limit ({{current}}/{{max}} files).',
            {
              filename: truncateFilename(originalFileName),
              current: newTotal,
              max: MAX_FILES,
            }
          ),
          'warning',
          5000
        );
      } else {
        showSnackBar(
          t('Uploading "{{filename}}" ({{current}}/{{max}} files)', {
            filename: truncateFilename(originalFileName),
            current: newTotal,
            max: MAX_FILES,
          }),
          'info',
          3000
        );
      }
    } catch (err: unknown) {
      if (err instanceof Error) {
        console.error(err.message);
      }
    } finally {
      setIsKnowledgeFilesLoading(false);
    }
  };

  return (
    <div>
      <WorkflowStepLabel
        stepLabel={stepLabel}
        label={label ?? t('Agent Knowledge')}
        description={description}
      />

      {allowExistingKnowledgeSelection && (
        <StyledFormItem>
          <RadioGroup
            aria-label={t('Knowledge base source')}
            name="knowledge_mode"
            value={knowledgeMode}
            onChange={event =>
              onKnowledgeModeChange(event.target.value as KnowledgeMode)
            }
          >
            <FormControlLabel
              value="create"
              control={<Radio />}
              label={
                hasAttachedKnowledge
                  ? t('Keep the current knowledge base')
                  : t('Create a new knowledge base')
              }
            />
            <FormControlLabel
              value="existing"
              control={<Radio />}
              label={t('Use an existing knowledge base')}
            />
            <FormControlLabel
              value="none"
              control={<Radio />}
              label={t('No knowledge base')}
            />
          </RadioGroup>
        </StyledFormItem>
      )}

      {knowledgeMode === 'none' && (
        <StyledFormItem>
          <Text type="body2" color="#666">
            {t(
              'Your agent will answer from its role and instructions only. You can attach a knowledge base later.'
            )}
          </Text>
        </StyledFormItem>
      )}

      {usingExistingKnowledge && (
        <StyledFormItem>
          <Text type="label">{t('Select a knowledge base')}</Text>
          <Dropdown
            name="existing_knowledge_id"
            required={true}
            options={existingKnowledgeOptions}
            fullWidth
            withError={!!errors.existing_knowledge_id}
            helperText={
              !!errors.existing_knowledge_id
                ? t('A knowledge base is required')
                : ''
            }
          />
        </StyledFormItem>
      )}

      {showKnowledgeFields && (
        <>
          <StyledFormItem>
            <label>{t('Name')}</label>
            <Input
              name="knowledge_name"
              required
              withError={!!errors.knowledge_name}
              helperText={
                !!errors.knowledge_name ? t('Knowledge name is required') : ''
              }
              onChange={event => {
                onFieldChange &&
                  onFieldChange('knowledge_name', event.target.value);
              }}
            />
          </StyledFormItem>

          <StyledFormItem>
            <Text type="label">
              {t('What will be the main language of your files?')}
            </Text>

            <Dropdown
              name="knowledge_language"
              required={true}
              options={knowledgeLanguageOptions}
              fullWidth
              withError={!!errors.knowledge_language}
              helperText={
                !!errors.knowledge_language
                  ? t("Knowledge's main language is required")
                  : ''
              }
              onChange={event => {
                if (onFieldChange)
                  onFieldChange('knowledge_language', event.target.value);
              }}
            />
          </StyledFormItem>

          {showAccessFields && (
            <ResourceAccessFields
              ownerUserId={ownerUserId}
              resourceLabelKey="knowledge base"
            />
          )}
        </>
      )}

      {/* Upload area. Hidden, not unmounted, during a search so FilePond
          keeps the staged files. */}
      {showKnowledgeFields && (
        <StyledFormItem hidden={isSearchActive} marginBottom="0">
          <label
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <span>
              {t('Upload the knowledge file(s) in PDF or CSV format.')}
            </span>
            <span
              style={{
                fontSize: '0.75rem',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: '999px',
                letterSpacing: '0.02em',
                color:
                  totalFileCount >= MAX_FILES
                    ? '#d32f2f'
                    : totalFileCount >= MAX_FILES - 25
                      ? '#e65100'
                      : 'var(--primary-color)',
                backgroundColor:
                  totalFileCount >= MAX_FILES
                    ? '#fdecea'
                    : totalFileCount >= MAX_FILES - 25
                      ? '#fff3e0'
                      : 'rgba(24, 65, 255, 0.08)',
              }}
            >
              {totalFileCount}/{MAX_FILES}
            </span>
          </label>

          {disableFileControls && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                'This knowledge base is currently being updated automatically from your website. File uploads and deletions are temporarily disabled until the update finishes.'
              )}
            </Alert>
          )}

          {/* UI to upload new knowledge files. */}
          <StyledFilePond
            ref={filePondRef}
            acceptedFileTypes={['application/pdf', 'text/csv']}
            allowFileEncode={true}
            allowMultiple={true}
            chunkSize={1024 * 1024}
            disabled={disableFileControls}
            files={files}
            labelIdle={t(
              'Drag and drop your files here or <span class="filepond--label-action">browse</span>.'
            )}
            maxFiles={MAX_FILES}
            maxFileSize="20MB"
            maxParallelUploads={4}
            name="knowledge_files"
            onupdatefiles={(fileItems: FilePondFile[]) => {
              setFiles(fileItems.map((f: FilePondFile) => f.file as File));
            }}
            onwarning={(error: { body: string }) => {
              if (error?.body === 'Max files') {
                showSnackBar(
                  t(
                    'File limit of {{max}} reached. Remove files to add more.',
                    {
                      max: MAX_FILES,
                    }
                  ),
                  'error',
                  6000
                );
                setIsKnowledgeFilesLoading(false);
              }
            }}
            onaddfile={async (error, file) => {
              await handleAddFile(error, file);
              if (!error && onFieldChange) {
                onFieldChange('knowledge_files', file?.file?.name);
              }
            }}
            onaddfilestart={() => {
              setIsKnowledgeFilesLoading(true);
            }}
            onremovefile={(error, file) => {
              setIsKnowledgeFilesLoading(false);

              if (file?.file?.name) {
                setFiles(prev => prev.filter(f => f.name !== file.file.name));

                setKnowledgeFilesData(prev => {
                  const filtered = prev.filter(
                    item => item.name !== file.file.name
                  );
                  newFileCountRef.current = filtered.length;
                  return filtered;
                });
              }
            }}
            server={null}
          />
        </StyledFormItem>
      )}

      {showKnowledgeFields && knowledgeId && (
        <StyledFormItem marginBottom="0">
          <KnowledgeFileSearchBar
            knowledgeId={knowledgeId}
            onActiveChange={setIsSearchActive}
            onDownload={fileName => {
              if (isKnowledgeFilesLoading || !downloadKnowledgeFileCallback)
                return;
              downloadKnowledgeFileCallback(fileName);
            }}
          />
        </StyledFormItem>
      )}

      {/* List of knowledge files of a knowledge document, replaced by the
          search results during a search. */}
      {showKnowledgeFields && (
        <StyledFormItem hidden={isSearchActive}>
          {/* Manually uploaded files: editable (download + delete). Body-level
              titles keep section headers below the page h2 hierarchy. */}
          <Accordion
            defaultOpen={manualFileEntries.length + filesNames.length > 0}
            title={
              <Text type="body1" bold lineHeight={1.4}>
                {t('Manually uploaded files ({{count}})', {
                  count: manualFileEntries.length + filesNames.length,
                })}
              </Text>
            }
          >
            {manualFileEntries.length + filesNames.length > 0 ? (
              <StyledKnowledgeItems>
                {/* Show newly uploaded files from session storage */}
                {filesNames.map((file, index) => (
                  <li key={`new-${index}`}>
                    <Tooltip title={file.name} arrow placement="top">
                      <StyledFileName>
                        {truncateFilename(file.name)}
                      </StyledFileName>
                    </Tooltip>
                    <Stack direction="row" gap="1rem" alignItems="center">
                      <span style={{ color: '#666', fontSize: '0.875rem' }}>
                        {t('Ready to upload')}
                      </span>
                    </Stack>
                  </li>
                ))}
                {/* Show existing, manually-uploaded knowledge files */}
                {manualFileEntries.map(([key, value]) => {
                  const isPendingDeletion = pendingDeletions.includes(key);

                  return (
                    <li key={key}>
                      <Tooltip title={key} key={key} arrow placement="top">
                        <StyledFileName
                          style={
                            isPendingDeletion
                              ? {
                                  color: '#999',
                                  textDecoration: 'line-through',
                                }
                              : undefined
                          }
                        >
                          {truncateFilename(key)}
                        </StyledFileName>
                      </Tooltip>
                      <Stack direction="row" gap="1rem" alignItems="center">
                        {isPendingDeletion ? (
                          <span
                            role="button"
                            tabIndex={0}
                            style={{
                              cursor: 'pointer',
                              color: 'var(--primary-color)',
                              fontSize: '0.875rem',
                              fontWeight: 600,
                            }}
                            onClick={() =>
                              unmarkKnowledgeFileForDeletion?.(key)
                            }
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                unmarkKnowledgeFileForDeletion?.(key);
                              }
                            }}
                          >
                            {t('Undo')}
                          </span>
                        ) : (
                          <>
                            <span
                              style={{ color: '#666', fontSize: '0.875rem' }}
                            >
                              {`${value.file_size} kB`}
                            </span>
                            <StyledDeleteIcon
                              title={t('Download file')}
                              onClick={() => {
                                if (
                                  isKnowledgeFilesLoading ||
                                  !downloadKnowledgeFileCallback
                                )
                                  return;
                                downloadKnowledgeFileCallback(key);
                              }}
                            >
                              <DownloadIcon width={16} height={16} />
                            </StyledDeleteIcon>
                            <StyledDeleteIcon
                              title={
                                disableFileControls
                                  ? t(
                                      'Disabled while an automatic update is in progress'
                                    )
                                  : t('Delete file')
                              }
                              aria-disabled={disableFileControls}
                              style={
                                disableFileControls
                                  ? { opacity: 0.4, cursor: 'not-allowed' }
                                  : undefined
                              }
                              onClick={() => {
                                if (disableFileControls) return;
                                if (!deleteKnowledgeFileCallback) return;
                                deleteKnowledgeFileCallback(key);
                              }}
                            >
                              <DeleteIcon width={16} height={16} />
                            </StyledDeleteIcon>
                          </>
                        )}
                      </Stack>
                    </li>
                  );
                })}
              </StyledKnowledgeItems>
            ) : (
              <Text type="body2" color="#64748B" lineHeight={1.5}>
                {t(
                  'No files uploaded yet. Use the drop zone above to add PDF or CSV files.'
                )}
              </Text>
            )}
          </Accordion>

          <Divider
            sx={{ my: '1.25rem', borderColor: 'rgba(15, 23, 42, 0.08)' }}
          />

          {showWebscrapConfig && !usingExistingKnowledge && (
            <WebscrapConfigFields />
          )}

          {/* Automated web-scrape files: read-only, download only. Always
              shown, even with zero files, so users know the section exists.
              Open by default only when it already has files, so an editor
              landing on a fresh knowledge base isn't met with an empty panel. */}
          <Accordion
            defaultOpen={scrapedFileEntries.length > 0}
            title={
              <Text type="body1" bold lineHeight={1.4}>
                {t('Imported from website ({{count}})', {
                  count: scrapedFileEntries.length,
                })}
              </Text>
            }
          >
            {scrapedFileEntries.length > 0 ? (
              <Stack gap="0.75rem">
                <Text type="body2" color="#64748B" lineHeight={1.5}>
                  {t(
                    'These files were automatically imported from your website and can only be downloaded.'
                  )}
                </Text>
                <StyledKnowledgeItems>
                  {scrapedFileEntries.map(([key, value]) => (
                    <li key={key}>
                      <Tooltip title={key} arrow placement="top">
                        <StyledFileName>{truncateFilename(key)}</StyledFileName>
                      </Tooltip>
                      <Stack direction="row" gap="1rem" alignItems="center">
                        <span style={{ color: '#666', fontSize: '0.875rem' }}>
                          {`${value.file_size} kB`}
                        </span>
                        <StyledDeleteIcon
                          title={t('Download file')}
                          onClick={() => {
                            if (
                              isKnowledgeFilesLoading ||
                              !downloadKnowledgeFileCallback
                            )
                              return;
                            downloadKnowledgeFileCallback(key);
                          }}
                        >
                          <DownloadIcon width={16} height={16} />
                        </StyledDeleteIcon>
                      </Stack>
                    </li>
                  ))}
                </StyledKnowledgeItems>
              </Stack>
            ) : (
              <Text type="body2" color="#64748B" lineHeight={1.5}>
                {t('No files have been imported from your website yet.')}
              </Text>
            )}
          </Accordion>
        </StyledFormItem>
      )}
    </div>
  );
};

export default AgentKnowledgeStep;
